import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import {
	CalendarIcon,
	ClockIcon,
	CubeIcon,
	UserGroupIcon,
	PencilIcon,
	TrashIcon,
	TagIcon,
} from '@heroicons/react/24/outline';

import useAsync from '../../hooks/useAsync';
import useModal from '../../hooks/useModal';

import Slider from '../../components/Shell/Slider';
import Button from '../../components/Button';
import Badge from '../../components/Badge';
import {
	getUserFullName,
	getStandardAssetName,
} from '../../components/helpers/functions';

import { getReservation, deleteReservation } from '../../api/reservations';

const MODAL_NAME = 'bookingOverview';

const getStatusBadge = (startTime, endTime) => {
	const now = moment();
	const start = moment(startTime);
	const end = moment(endTime);

	if (now.isBefore(start)) {
		return { status: 'info', label: 'Upcoming' };
	} else if (now.isAfter(end)) {
		return { status: 'success', label: 'Completed' };
	} else {
		return { status: 'warning', label: 'In Progress' };
	}
};

export default () => {
	const navigate = useNavigate();
	const { open, setClose } = useModal(MODAL_NAME);
	const { setOpen: openAddBooking } = useModal('addBooking');
	const { setClose: closeDeleteModal, setOpen: openDeleteModal } =
		useModal('deleteModal');

	const {
		value: reservation,
		execute: onGetReservation,
		status: isLoading,
	} = useAsync(getReservation, { immediate: false });

	const { execute: onDeleteReservation } = useAsync(deleteReservation, {
		immediate: false,
	});

	useEffect(() => {
		if (open?.id) {
			onGetReservation({ id: open.id });
		}
	}, [open?.id]);

	const handleEdit = () => {
		setClose();
		openAddBooking({ id: reservation.id, edit: true, path: '/bookings' });
	};

	const handleDelete = () => {
		openDeleteModal({
			title: 'Delete Booking',
			description:
				'Are you sure you want to delete this booking? This action cannot be undone.',
			confirmText: 'Delete Booking',
			onConfirm: () =>
				onDeleteReservation({ id: reservation.id }, () => {
					closeDeleteModal();
					setClose();
					navigate('/bookings');
				}),
		});
	};

	const { status: badgeStatus, label: badgeLabel } = reservation
		? getStatusBadge(reservation.startTime, reservation.endTime)
		: { status: 'default', label: '' };

	// Check if this is a multi-day reservation
	const isMultiDay = reservation
		? !moment(reservation.startTime).isSame(moment(reservation.endTime), 'day')
		: false;

	// Check if this is an all-day reservation (starts at midnight and ends near midnight)
	const isAllDay = reservation
		? moment(reservation.startTime).hours() === 0 &&
			moment(reservation.startTime).minutes() === 0 &&
			moment(reservation.endTime).hours() === 23 &&
			moment(reservation.endTime).minutes() === 59
		: false;

	const title = 'Booking Details';

	console.log('reservation: ', reservation);

	const handleClose = () => {
		// Navigate back to the base path when slider closes
		if (open?.path) {
			navigate(open.path);
		}
	};

	return (
		<Slider
			name={MODAL_NAME}
			onClose={handleClose}
			title={
				<div className="flex items-center justify-between">
					<div className="flex items-center">
						{title}
						<Badge isBeta size="small" className="ml-3" status={badgeStatus}>
							{badgeLabel}
						</Badge>
					</div>
					{/* Header with Status and Actions */}
					<div className="flex items-center justify-end">
						<div className="flex items-center space-x-2">
							<Button type="secondary" onClick={handleEdit}>
								<PencilIcon className="h-4 w-4 mr-1" />
								Edit
							</Button>
							<Button type="danger" onClick={handleDelete}>
								<TrashIcon className="h-4 w-4 mr-1" />
								Delete
							</Button>
						</div>
					</div>
				</div>
			}
			wide
		>
			{isLoading === 'pending' ? (
				<div className="animate-pulse space-y-4">
					<div className="h-4 bg-gray-200 rounded w-3/4"></div>
					<div className="h-4 bg-gray-200 rounded w-1/2"></div>
					<div className="h-4 bg-gray-200 rounded w-5/6"></div>
				</div>
			) : reservation ? (
				<div className="space-y-6">
					{/* Date & Time Info */}
					<div className="bg-gray-50 rounded-lg p-4">
						<div className="grid grid-cols-2 gap-4">
							<div className="flex items-start">
								<CalendarIcon className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
								<div>
									<p className="text-sm font-medium text-gray-900">
										{isMultiDay ? 'Start Date' : 'Date'}
									</p>
									<p className="text-sm text-gray-600">
										{moment(reservation.startTime).format('dddd, MMMM D, YYYY')}
									</p>
									{isMultiDay && (
										<>
											<p className="text-sm font-medium text-gray-900 mt-2">
												End Date
											</p>
											<p className="text-sm text-gray-600">
												{moment(reservation.endTime).format(
													'dddd, MMMM D, YYYY',
												)}
											</p>
										</>
									)}
								</div>
							</div>
							<div className="flex items-start">
								<ClockIcon className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
								<div>
									<p className="text-sm font-medium text-gray-900">Time</p>
									{isAllDay ? (
										<p className="text-sm text-gray-600">All Day</p>
									) : (
										<p className="text-sm text-gray-600">
											{moment(reservation.startTime).format('h:mm A')} -{' '}
											{moment(reservation.endTime).format('h:mm A')}
										</p>
									)}
									<p className="text-xs text-gray-500">
										Duration:{' '}
										{moment
											.duration(
												moment(reservation.endTime).diff(
													moment(reservation.startTime),
												),
											)
											.humanize()}
									</p>
								</div>
							</div>
						</div>
					</div>

					{/* Assets Section */}
					<div>
						<div className="flex items-center mb-3">
							<CubeIcon className="h-5 w-5 text-gray-400 mr-2" />
							<h3 className="text-sm font-medium text-gray-900">
								Reserved Assets ({reservation.assets?.length || 0})
							</h3>
						</div>
						{reservation.assets?.length > 0 ? (
							<div className="bg-white border rounded-lg divide-y">
								{reservation.assets.map((asset) => {
									console.log('asset: ', asset, {
										...asset,
										manufacturerName:
											asset?.manufacturerName || asset?.manufacturer?.name,
									});
									return (
										<div
											key={asset.id}
											className="p-3 flex items-center hover:bg-gray-50 cursor-pointer"
											onClick={() => {
												setClose();
												navigate(`/assets/${asset.id}/overview`);
											}}
										>
											<div className="w-8 h-8 rounded-full bg-cyan-100 flex items-center justify-center mr-3">
												<CubeIcon className="h-4 w-4 text-cyan-600" />
											</div>
											<div>
												<p className="text-sm font-medium text-gray-900">
													{getStandardAssetName({
														...asset,
														manufacturerName:
															asset?.manufacturerName ||
															asset?.manufacturer?.name,
													})}
												</p>
												{asset.serialNumber && (
													<p className="text-xs text-gray-500">
														S/N: {asset.serialNumber}
													</p>
												)}
											</div>
										</div>
									);
								})}
							</div>
						) : (
							<p className="text-sm text-gray-500">No assets assigned</p>
						)}
					</div>

					{/* Users Section */}
					<div>
						<div className="flex items-center mb-3">
							<UserGroupIcon className="h-5 w-5 text-gray-400 mr-2" />
							<h3 className="text-sm font-medium text-gray-900">
								Assigned Users ({reservation.users?.length || 0})
							</h3>
						</div>
						{reservation.users?.length > 0 ? (
							<div className="bg-white border rounded-lg divide-y">
								{reservation.users.map((user) => (
									<div key={user.id} className="p-3 flex items-center">
										<div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center mr-3">
											<span className="text-sm font-medium text-green-600">
												{getUserFullName(user)
													.split(' ')
													.map((n) => n[0])
													.join('')
													.toUpperCase()}
											</span>
										</div>
										<div>
											<p className="text-sm font-medium text-gray-900">
												{getUserFullName(user)}
											</p>
											{user.email && (
												<p className="text-xs text-gray-500">{user.email}</p>
											)}
										</div>
									</div>
								))}
							</div>
						) : (
							<p className="text-sm text-gray-500">No users assigned</p>
						)}
					</div>

					{/* Power Users Section */}
					{reservation.powerUsers?.length > 0 && (
						<div>
							<div className="flex items-center mb-3">
								<UserGroupIcon className="h-5 w-5 text-gray-400 mr-2" />
								<h3 className="text-sm font-medium text-gray-900">
									Power Users ({reservation.powerUsers.length})
								</h3>
							</div>
							<div className="bg-white border rounded-lg divide-y">
								{reservation.powerUsers.map((powerUser) => (
									<div key={powerUser.id} className="p-3 flex items-center">
										<div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center mr-3">
											<span className="text-sm font-medium text-purple-600">
												{`${powerUser.firstName || ''} ${powerUser.lastName || ''}`
													.trim()
													.split(' ')
													.map((n) => n[0])
													.join('')
													.toUpperCase() || '?'}
											</span>
										</div>
										<div>
											<p className="text-sm font-medium text-gray-900">
												{`${powerUser.firstName || ''} ${powerUser.lastName || ''}`.trim() ||
													'Unknown'}
											</p>
											{powerUser.email && (
												<p className="text-xs text-gray-500">
													{powerUser.email}
												</p>
											)}
										</div>
									</div>
								))}
							</div>
						</div>
					)}

					{/* Category Section */}
					{reservation.category && (
						<div>
							<div className="flex items-center mb-3">
								<TagIcon className="h-5 w-5 text-gray-400 mr-2" />
								<h3 className="text-sm font-medium text-gray-900">Category</h3>
							</div>
							<div className="bg-white border rounded-lg p-3">
								<p className="text-sm text-gray-900">
									{reservation.category.name}
								</p>
							</div>
						</div>
					)}

					{/* Metadata */}
					<div className="border-t pt-4 text-xs text-gray-500">
						<div className="flex justify-between">
							<span>
								Created{' '}
								{moment(reservation.createdAt).format('MMM D, YYYY h:mm A')}
							</span>
							{reservation.updatedAt &&
								reservation.updatedAt !== reservation.createdAt && (
									<span>
										Last updated{' '}
										{moment(reservation.updatedAt).format('MMM D, YYYY h:mm A')}
									</span>
								)}
						</div>
					</div>
				</div>
			) : (
				<p className="text-sm text-gray-500">Booking not found</p>
			)}
		</Slider>
	);
};
